// Aquifers + lush caves (GEN-6 extension). PURE + deterministic (ENG-1/ENG-3).
//
// The M2 carver hollows caves and always leaves air. Two salted 2D fields make
// that hollow interesting without touching the carve shape at all — so a world
// generated without them is byte-identical:
//
//  - **Aquifers**: a local water table Y per column. A carved cell at or below its
//    column's table floods instead of emptying, so deep cave systems come out part
//    flooded and a tunnel can open into a submerged cavern. The table is capped
//    well under the surface, so surface caves stay dry.
//  - **Lush caves**: a "lushness" field. Where it runs high, a carved cavern's
//    floor grows moss and its ceiling hangs glow lichen and berry-bearing cave
//    vines — a lit, living pocket instead of bare stone.
import { AQUIFER, LUSH } from '../../config';
import { defineSalt } from '../../engine/rng';
import { Simplex, fbm2 } from '../../engine/noise';

const S_AQUIFER = defineSalt('cave-aquifer');
const S_LUSH = defineSalt('cave-lush');

/** Block-state ids the cave decoration places; absent groups are simply skipped. */
export interface CaveFeatureIds {
  /** Flood state for aquifer cells (the world's water). */
  water?: number;
  lush?: {
    moss: number;
    mossCarpet: number;
    glowLichen: number;
    caveVines: number;
    caveVinesBerries: number;
    /** The deeper dressing, all optional so a registry missing any of it still
     *  generates the moss/lichen/vine cave exactly as before. */
    clay?: number;
    bigDripleaf?: number;
    smallDripleaf?: number;
    rootedDirt?: number;
    hangingRoots?: number;
    sporeBlossom?: number;
    azalea?: number;
    floweringAzalea?: number;
  };
}

export class CaveFeatures {
  private readonly table: Simplex;
  private readonly lushness: Simplex;

  constructor(seed: number | string | bigint, readonly ids: CaveFeatureIds) {
    this.table = new Simplex(`${seed}#${S_AQUIFER}`);
    this.lushness = new Simplex(`${seed}#${S_LUSH}`);
  }

  /** Whether any feature is configured (an empty config ⇒ plain M2 caves). */
  get active(): boolean {
    return this.ids.water !== undefined || this.ids.lush !== undefined;
  }

  /** The local water-table Y for a column — the surface of its aquifer. */
  waterTableY(x: number, z: number): number {
    const n = fbm2(this.table, x, z, { frequency: AQUIFER.FREQ, octaves: 2 });
    return Math.round(AQUIFER.BASE_Y + n * AQUIFER.AMP);
  }

  /** Does a carved cell at (x,y,z) flood rather than empty? */
  floods(x: number, y: number, z: number): boolean {
    if (this.ids.water === undefined) return false;
    if (y > AQUIFER.MAX_Y) return false; // never near the surface
    return y <= this.waterTableY(x, z);
  }

  /** Raw lushness at a column (exposed for tests/tuning). */
  lushnessAt(x: number, z: number): number {
    return fbm2(this.lushness, x, z, { frequency: LUSH.FREQ, octaves: 2 });
  }

  /** Is this column inside a lush-cave pocket? */
  isLush(x: number, z: number): boolean {
    return this.ids.lush !== undefined && this.lushnessAt(x, z) > LUSH.THRESHOLD;
  }
}
